<script setup lang="ts">
  import { onMounted, onUnmounted } from "vue";
  import { useI18n } from "vue-i18n";
  import { Text, IconArrowLeft, IconGunKey, IconWireless, IconChevronRight, useNotification, IconClear } from "@ui-kit";
  import { usePowerRfid, useReadRfid } from "@plugins";
  import { useProfileOptions } from "@src/hooks";
  import { TopBar, StatusBar } from "@src/components";
  import { useCheckAlarming } from "./useCheckAlarming";

  const emit = defineEmits<{
    "close": [value: void];
    "activate:mode_single": [value: void];
  }>();

  const powerRfid = usePowerRfid();
  const readRfid = useReadRfid();

  const { profile } = useProfileOptions();
  const { show } = useNotification();
  const { t } = useI18n();
  const {
    add,
    clear,
    epcWithoutPairCount,
    alarmWithoutPairCount,
    epcClearCount: epcCleanCount,
    alarmClearCount: alarmCleanCount,
    unknownCount,
    pairCount,
  } = useCheckAlarming();

  const handleToSingleMode = () => {
    if (readRfid.isScanning.value) {
      show({ title: t("Остановите сканирование"), type: "warning" });
    } else {
      emit("activate:mode_single");
    }
  };

  onMounted(async () => {
    await readRfid.setMethod("multiple");
    await powerRfid.set(profile.value.constants.rfid.power_multi_read);

    readRfid.subscribe((data) => add(data.value));
  });

  onUnmounted(async () => {
    await powerRfid.set(powerRfid.prevPower.value);
    await readRfid.setMethod("singleShort");

    readRfid.unsubscribe();
  });
</script>

<template>
  <div class="check-alarming-multi" data-testid="check-alarming-multi">
    <StatusBar />
    <TopBar
      :title="$t('Проверка кодировки алармов')"
      @click:left="
        () => {
          if (!readRfid.isScanning.value) {
            emit('close');
          }
        }
      "
      @click:right="
        () => {
          if (!readRfid.isScanning.value) {
            clear();
          }
        }
      "
      data-testid="top-bar-check-alarming"
    >
      <template #left>
        <IconArrowLeft :style="{ opacity: readRfid.isScanning.value ? 0.4 : 1 }" />
      </template>
      <template #right>
        <IconClear :style="{ opacity: readRfid.isScanning.value ? 0.4 : 1 }" />
      </template>
    </TopBar>
    <div class="widget-check-alarming__content">
      <div class="widget-check-alarming__details">
        <div class="widget-check-alarming__details-row">
          <div class="widget-check-alarming__tile" @click="handleToSingleMode" data-testid="pair-tile">
            <div class="tile__epc-alarm">
              <div class="tile__epc-alarm__info">
                <Text>{{ $t("Пара EPC+Alarm") }}</Text>
                <Text class="widget-check-alarming__tile-count" data-testid="pair-count">{{ pairCount }}</Text>
              </div>
              <IconChevronRight
                :style="`width: 32px; height: 32px; stroke: ${
                  readRfid.isScanning.value ? 'var(--sf-base-text-secondary)' : 'var(--sf-base-text-primary)'
                };`"
              />
            </div>
          </div>
        </div>
        <div class="widget-check-alarming__details-row">
          <div class="widget-check-alarming__tile">
            <div class="widget-check-alarming__tile-title">
              <Text font="body-regular-14">{{ $t("EPC") }}</Text>
              <Text color="secondary" font="body-regular-14"> {{ $t("без аларма") }}</Text>
            </div>
            <Text class="widget-check-alarming__tile-count--small" data-testid="epc-count">{{ epcWithoutPairCount }}</Text>
          </div>
          <div class="widget-check-alarming__tile">
            <div class="widget-check-alarming__tile-title">
              <Text font="body-regular-14">{{ $t("Аларм") }}</Text>
              <Text color="secondary" font="body-regular-14">{{ $t("без EPC") }}</Text>
            </div>
            <Text class="widget-check-alarming__tile-count--small" data-testid="alarm-count">{{ alarmWithoutPairCount }}</Text>
          </div>
        </div>
        <div class="widget-check-alarming__details-row">
          <div
            class="widget-check-alarming__tile"
            :class="{
              'widget-check-alarming__details-item--error': epcCleanCount > 0,
            }"
          >
            <Text font="body-regular-14" :color="epcCleanCount > 0 ? 'error' : 'primary'">
              {{ $t("Чистые EPC") }}
            </Text>
            <Text
              class="widget-check-alarming__tile-count--small"
              :color="epcCleanCount > 0 ? 'error' : 'primary'"
              data-testid="epc-clean-count"
            >
              {{ epcCleanCount }}
            </Text>
          </div>
          <div
            class="widget-check-alarming__tile"
            :class="{
              'widget-check-alarming__details-item--error': alarmCleanCount > 0,
            }"
          >
            <Text font="body-regular-14" :color="alarmCleanCount > 0 ? 'error' : 'primary'">
              {{ $t("Чистые алармы") }}
            </Text>
            <Text
              class="widget-check-alarming__tile-count--small"
              :color="alarmCleanCount > 0 ? 'error' : 'primary'"
              data-testid="alarm-clean-count"
            >
              {{ alarmCleanCount }}
            </Text>
          </div>
        </div>
        <div class="widget-check-alarming__details-row--unknown">
          <Text color="secondary" font="body-regular-14">{{ $t("Неизвестные EPC") }}</Text>
          <Text color="secondary" font="body-regular-18" data-testid="unknown-count">{{ unknownCount }}</Text>
        </div>
        <div class="widget-check-alarming__status">
          <template v-if="readRfid.isScanning.value">
            <IconWireless />
            <Text color="warning" font="body-regular-12">{{ $t("Идет сканирование") }}</Text>
          </template>
          <template v-else>
            <IconGunKey />
            <Text color="secondary" font="body-regular-12">{{ $t("Нажмите на курок для начала сканирования") }}</Text>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .check-alarming-multi {
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
    background-color: var(--sf-base-background-primary);
  }

  .widget-check-alarming__content {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    padding: 16px;
    overflow: hidden;
  }

  .widget-check-alarming__details {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
  }

  .widget-check-alarming__details-row {
    display: flex;
    gap: 8px;
  }

  .widget-check-alarming__details-row--unknown {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 8px;
  }

  .widget-check-alarming__tile {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 8px;
    padding: 16px;
    background-color: var(--sf-base-background-secondary);
    border-radius: 16px;
  }

  .widget-check-alarming__tile-title {
    display: flex;
    gap: 4px;
  }

  .widget-check-alarming__tile-count {
    font-weight: 580;
    font-size: 40px;
    line-height: 52px;
  }

  .widget-check-alarming__tile-count--small {
    font-weight: 580;
    font-size: 32px;
    line-height: 42px;
  }

  .widget-check-alarming__details-item--error {
    background-color: var(--sf-base-background-error);
  }

  .tile__epc-alarm {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }

  .tile__epc-alarm__info {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 8px;
  }

  .widget-check-alarming__status {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 16px 16px;
    gap: 8px;
    margin-top: auto;
  }
</style>
